import { useId, useState, type ReactNode } from "react";
import type { SurveySurface } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { degrees, humanise } from "../lib/format.js";
import {
  ADJACENCY_TEMPERATURE_SOURCE,
  WALL_CHOICE_LABEL,
  confirmationByline,
  parseAdjacencyTemperature,
  type WallChoice,
} from "../lib/wall-adjacency.js";
import { Failure } from "./Async.jsx";

/**
 * The "Other side" cell of a wall in the Survey page's Rooms tables — and in
 * the scan's Walls table under the 3D view, for the same heat-loss wall, so
 * the question can be answered while looking at the wall. Both read the same
 * survey and both re-read it once answered, so they cannot disagree.
 *
 * Three states, and the page must never blur them:
 *
 *  - ASSUMED — the scan conversion called the wall external only because
 *    nothing was scanned behind it. A compact marker, "Outside wall? — may be
 *    a party wall", and (for someone who may change the survey) the three
 *    answers: party wall, unheated space, or confirm it is outside.
 *  - CONFIRMED — a person answered. What it is, and "confirmed by a surveyor"
 *    with who and when.
 *  - ANYTHING ELSE — as before: the adjacency and its temperature.
 *
 * A party wall or an unheated space needs the temperature on the other side
 * and there is no default: MCS publishes none (ADJACENCY_TEMPERATURE_SOURCE
 * says where it does come from). The box starts empty and the answer cannot
 * be saved without it.
 */
export function WallAdjacencyCheck({
  surface,
  roomName,
  canAnswer,
  onAnswered,
  anchor = true,
}: {
  readonly surface: SurveySurface;
  readonly roomName: string;
  /** `survey: update`, on a survey that is still a draft. */
  readonly canAnswer: boolean;
  /** The answer is saved: re-read the survey and the heat loss. */
  readonly onAnswered: () => void;
  /**
   * Carry the `adjacency-<surface id>` element id. Once per page: the Rooms
   * table's copy is the anchor; a second copy of the same wall sets false.
   */
  readonly anchor?: boolean;
}): ReactNode {
  // A FLOOR can be flagged too (the conversion assumes the lowest storey's
  // floor is solid and on the ground), but none of the three answers below is
  // one a floor can take and the copy is about walls. It gets a marker saying
  // what was assumed and why it matters, and is answered in the floor's own
  // panel beside the plan, where the floor's five answers are.
  if (surface.adjacencyAssumed === true && surface.kind !== "wall") {
    return (
      <span className="prov prov-assumed" data-testid="adjacency-assumed-floor">
        Assumed solid, on the ground
        <span className="muted small">
          {" "}
          — a suspended floor is much colder (MIS 3005-D §3.4.1(d) rather than
          §3.4.1(c)). Set it in the floor&rsquo;s panel beside the plan.
        </span>
      </span>
    );
  }
  if (surface.adjacencyAssumed === true) {
    return (
      <AssumedWall
        surface={surface}
        roomName={roomName}
        canAnswer={canAnswer}
        onAnswered={onAnswered}
        anchor={anchor}
      />
    );
  }
  return (
    <>
      {humanise(surface.adjacency)}
      {surface.adjacencyTempC === null ? null : (
        <span className="muted small"> · {degrees(surface.adjacencyTempC)}</span>
      )}
      {surface.adjacencyConfirmed ? (
        <>
          {" "}
          <span
            className="prov prov-confirmed"
            data-testid="adjacency-confirmed"
            title={`What is on the other side of this wall was confirmed by a surveyor (${confirmationByline(surface.adjacencyConfirmed)}).`}
          >
            Confirmed by a surveyor
          </span>
          <span className="muted small"> ({confirmationByline(surface.adjacencyConfirmed)})</span>
        </>
      ) : null}
    </>
  );
}

function AssumedWall({
  surface,
  roomName,
  canAnswer,
  onAnswered,
  anchor,
}: {
  readonly surface: SurveySurface;
  readonly roomName: string;
  readonly canAnswer: boolean;
  readonly onAnswered: () => void;
  readonly anchor: boolean;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [choice, setChoice] = useState<WallChoice | null>(null);
  const [typed, setTyped] = useState("");
  const [touched, setTouched] = useState(false);
  const inputId = useId();
  const noteId = useId();
  const wallName = surface.name ?? "this wall";

  const parsed = parseAdjacencyTemperature(typed);

  const answer = (body: Parameters<typeof client.setWallAdjacency>[1]) =>
    submit(async () => {
      await client.setWallAdjacency(surface.id, body);
      onAnswered();
    });

  return (
    <div
      className="adjacency-check"
      {...(anchor ? { id: `adjacency-${surface.id}` } : {})}
      data-testid="adjacency-check"
      role="group"
      aria-label={`What is on the other side of ${wallName} in ${roomName}`}
    >
      <p className="adjacency-check__flag">
        <strong>Outside wall?</strong> <span>— may be a party wall</span>
      </p>
      <p className="muted small adjacency-check__why">
        Nothing was scanned behind it, so the heat loss counts it as outside.
      </p>

      {!canAnswer ? null : choice === null ? (
        <div className="adjacency-check__actions">
          <button type="button" className="secondary small" disabled={busy} onClick={() => setChoice("party")}>
            {WALL_CHOICE_LABEL.party}
          </button>
          <button
            type="button"
            className="secondary small"
            disabled={busy}
            onClick={() => setChoice("unheated-space")}
          >
            {WALL_CHOICE_LABEL["unheated-space"]}
          </button>
          <button
            type="button"
            className="secondary small"
            disabled={busy}
            onClick={() => void answer({ adjacency: "external" })}
          >
            {busy ? "Saving…" : "Confirm outside wall"}
          </button>
        </div>
      ) : (
        <form
          className="adjacency-check__form"
          aria-label={WALL_CHOICE_LABEL[choice]}
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if ("value" in parsed) void answer({ adjacency: choice, temperatureC: parsed.value });
          }}
        >
          <p className="small adjacency-check__chosen">
            <strong>{WALL_CHOICE_LABEL[choice]}</strong>
          </p>
          <label htmlFor={inputId} className="small">
            {choice === "party"
              ? "Temperature in the adjoining home, °C"
              : "Temperature in the unheated space, °C"}
          </label>{" "}
          <input
            id={inputId}
            inputMode="decimal"
            // aria-required, not `required`: the browser's own "fill in this
            // field" bubble would pre-empt the message below, which says why.
            aria-required="true"
            aria-describedby={noteId}
            aria-invalid={touched && "error" in parsed}
            className="adjacency-check__temp"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            onBlur={() => setTouched(true)}
          />{" "}
          <button type="submit" className="small" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>{" "}
          <button
            type="button"
            className="secondary small"
            disabled={busy}
            onClick={() => {
              setChoice(null);
              setTyped("");
              setTouched(false);
            }}
          >
            Cancel
          </button>
          {touched && "error" in parsed ? <p className="hint small fail">{parsed.error}</p> : null}
          <p id={noteId} className="muted small adjacency-check__source">
            {ADJACENCY_TEMPERATURE_SOURCE}
          </p>
        </form>
      )}

      {error !== null ? <Failure error={error} what="that answer" /> : null}
    </div>
  );
}
